import { Component } from "react";
import styled from "styled-components";
import moment from "moment";
import messageGenerator from "@soltell/soltell-message-generator";

import * as sms from "../../services/Sms";
import IssueItem from "../IssueItem";
import MessageLogItem from "../MessagesManual/MessageLogItem";
import LocalStorageKeys from "../../localStorageKeys";
import DatePickerCombo from "../DatePickerCombo";

import { sendData } from "../../services/sendData";

const { fixedTs, extractIssues } = messageGenerator;

const ISSUES_DATA = Object.fromEntries(
  Object.entries(messageGenerator.getIssuesData()).map(([key, val]) => {
    const sortedVal = [
      ["", { msg: "", severity: 0 }],
      ["ok", { msg: "", severity: 0 }],
      ...Object.entries(val),
    ]
      .map(([status, data]) => ({ status, data }))
      .sort(({ data: d1 }, { data: d2 }) => d1.severity - d2.severity);
    return [key, sortedVal];
  }),
);

const ARRAY_ISSUE_MAPPER = [
  { dbName: "if_invdeg", messageGenName: "if-invdeg-" },
  { dbName: "if_pow", messageGenName: "if-pow-" },
  { dbName: "if_inv_n", messageGenName: "if-inv-" },
  { dbName: "if_powdeg", messageGenName: "if-powdeg-" },
  { dbName: "if_latestart", messageGenName: "if-latestart-" },
];

const STRING_ISSUE_MAPPER = [
  { dbName: "f_mcm", messageGenName: "f-mcm" },
  { dbName: "f_shadow", messageGenName: "f-shadow" },
  { dbName: "f_erange", messageGenName: "f-erange" },
  { dbName: "f_prange", messageGenName: "f-prange" },
  { dbName: "f_vgrid", messageGenName: "f-vgrid" },
  { dbName: "f_fct", messageGenName: "f-fct" },
  { dbName: "f_vgridshift", messageGenName: "f-vgridshift" },
  { dbName: "f_ovld", messageGenName: "f-ovld" },
  { dbName: "f_shadow", messageGenName: "f-shadow" },
  { dbName: "f_dust", messageGenName: "f-dust" },
  { dbName: "f_intres", messageGenName: "f-intres" },
  { dbName: "f_comm", messageGenName: "f-comm" },
];

function extractArrayIssues(data) {
  const newData = [];
  for (const { dbName, messageGenName } of ARRAY_ISSUE_MAPPER) {
    if (Array.isArray(data[dbName])) {
      newData.push(
        ...data[dbName].reduce((acc, val, i) => {
          acc.push({
            dbName,
            messageGenName: `${messageGenName}${i + 1}`,
            status: val,
            dbIdx: i,
          });
          acc[`${messageGenName}${i + 1}`] = val;
          return acc;
        }, []),
      );
    }
  }

  return newData;
}

function extractStringIssues(data) {
  const newData = [];
  for (const { dbName, messageGenName } of STRING_ISSUE_MAPPER) {
    newData.push({ dbName, messageGenName, status: data[dbName] });
  }

  return newData;
}

function extractIssuesDb(data, inverters) {
  if (data) {
    const strRaw = extractStringIssues(data);
    const strIssues = extractIssues(
      Object.fromEntries(strRaw.map((e) => [e.messageGenName, e.status])),
      inverters,
      [],
    ).map((i) => {
      i.dbProps = {
        name: strRaw.find((e) => e.messageGenName === i.issueKey).dbName,
      };
      return i;
    });

    const arrRaw = extractArrayIssues(data);
    const arrayIssues = extractIssues(
      Object.fromEntries(arrRaw.map((e) => [e.messageGenName, e.status])),
      inverters,
      [],
    ).map((i) => {
      i.dbProps = i.originalIssuesNames.map((name) => {
        const { dbName, dbIdx } = arrRaw.find((e) => name === e.messageGenName);
        return { name: dbName, idx: dbIdx };
      });
      return i;
    });

    return [...strIssues, ...arrayIssues].map((issue) => {
      issue.dbId = data.id;
      issue.updateDb = false;
      return issue;
    });
  }
  return [];
}

const TRANSLATOR_VERSION = messageGenerator.version();

const DB_DATE_FORMAT = "YYYY-MM-DD";
const HUMAN_DATE_FORMAT = "DD/MM/YYYY";

const BASE_SYSHEALTH_THRESHOLD = 85;

const BASE_SEVERITY_THRESHOLD = 1;
const WARNING_SEVERITY_THRESHOLD = 2;
const ALERT_SEVERITY_THRESHOLD = 3;

const LIST_SEPERATOR = ",";
const DEFAULT_LNG = "he";

const ROLES_TO_SEND = ["admin", "client", "whitelabel"];
const ALERTS_TO_SEND = ["invStopAlert"];

const Section = styled.div`
  padding: 10px 0;
  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const getCleanIssueState = (
  id,
  issue,
  label,
  phonesString,
  emailsString,
  lng,
  sysHealth,
  customList,
  isMock = false,
) => {
  return {
    id: id,
    shouldSend: false,
    label: label,
    phonesString: phonesString,
    emailsString: emailsString,
    customList: customList,
    sendCustom: !!customList.length,
    lng: lng,
    sysHealth: sysHealth,
    isMock: isMock,
    ...issue,
  };
};

const LOG_STATUS = {
  pending: "pending",
  sending: "sending",
  success: "success",
  failure: "failure",
};

// constructor for object representing a message log
function MessageLog(
  sysId,
  recipient,
  emails,
  customList,
  status,
  body,
  sender,
  msgCode,
  dataDate,
  lng,
  error = null,
) {
  this.recipientNumber = [...recipient]; // recipient number
  this.emails = [...emails]; // recipient emails
  this.customList = [...customList];
  this.singlePhoneError = recipient.map(() => null); // errors for individual phone number
  this.singlePhoneStatus = recipient.map(() => status); // status for individual phone number
  this.messageId = recipient.map(() => ""); // aws message ids
  this.sysId = sysId || "";
  this.type = sms.getMessageTypes()[0]; // type of the message
  this.status = status; // log status
  this.body = body; // message body
  this.sender = sender; // sender alias
  this.msgCode = msgCode; // code of the automatic message
  this.error = error; // error if exists
  this.lng = lng; // log's language
  this.lastEdit = moment();
  this.dataDate = dataDate;
}

class BackendMessagesForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      isLoading: true,
      data: null,
      selectedMoment: moment(),
      dataMoment: null,
      dataUpdateMoment: null,
      issues: [],
      sendQueue: [],
      sendPromiseIdx: null,
      invalidConfiguration: false,
      showTestSystems: false,
      shouldSendMessages: true,
      shouldUpdateInsights: true,
      pushingInsights: false,
      pushedInsightsResponses: [],
    };
  }

  /**
   * returns an object with 2 keys
   * sysMatch - an object connecting sysIds with related orgs
   * orgMatch - an object connecting orgs with eligible user ids
   */
  matchOrgsToUsers = (orgsData, usersData, systems) => {
    const orgMatch = Object.fromEntries(
      Object.entries(orgsData).map(([orgId, orgObj]) => [
        orgId,
        { ...orgObj, users: [] },
      ]),
    );
    const sysMatch = Object.entries(usersData).reduce(
      (sysMatch, [phone, user]) => {
        if (ROLES_TO_SEND.includes(user.role)) {
          const orgId = user.organization;
          orgMatch[orgId].users.push(phone);

          orgsData[orgId].systemIds.forEach((sysId) => {
            if (!sysMatch[sysId]) {
              sysMatch[sysId] = [];
            }
            if (!sysMatch[sysId].includes(orgId)) {
              sysMatch[sysId].push(orgId);
            }
          });
        }
        return sysMatch;
      },
      Object.fromEntries(Object.keys(systems).map((sysId) => [sysId, []])),
    );

    return { orgMatch, sysMatch };
  };

  extractAllIssues = (rawData, systems, orgsData, usersData) => {
    const { sysMatch, orgMatch } = this.matchOrgsToUsers(
      orgsData,
      usersData,
      systems,
    );
    const invalidSystems = Object.entries(sysMatch).reduce(
      (invalidSystems, [sysId, orgs]) => {
        // if system has no orgs associated to it, save it as misconfigured
        if (!orgs.length) {
          invalidSystems.push(sysId);
        }
        return invalidSystems;
      },
      [],
    );
    const invalidOrgs = Object.entries(orgMatch).reduce(
      (invalidOrgs, [orgId, orgObj]) => {
        const noUsers = !orgObj.users.length;
        const noSystems = !orgObj.systemIds.length;
        if (noUsers || noSystems) {
          invalidOrgs.push({ id: orgId, noUsers, noSystems });
        }
        return invalidOrgs;
      },
      [],
    );
    const invalidConfiguration = !!(
      invalidSystems.length || invalidOrgs.length
    );
    if (invalidConfiguration) {
      console.warn("invalid configuration object", {
        invalidSystems,
        invalidOrgs,
      });
    }
    const [issues, badHealthSystems] = rawData.reduce(
      ([issues, badHealthSystems], data) => {
        const id = data.systemId;
        const sys = systems[id];
        if (!sys) {
          return [issues, badHealthSystems];
        }
        const rawSystemIssues = extractIssuesDb(data, sys.inverters);
        if (rawSystemIssues.length !== 0) {
          // get label and phones and emails for a system
          const { phones, emails, custom, label, lng } = sysMatch[id].reduce(
            (acc, sysOrgId) => {
              orgMatch[sysOrgId].users.forEach((phone) => {
                const user = usersData[phone];
                const presetSms = ALERTS_TO_SEND.some((alertType) => {
                  return user.alerts.toSend[alertType].methods.sms;
                });
                const presetEmail = ALERTS_TO_SEND.some((alertType) => {
                  return user.alerts.toSend[alertType].methods.email;
                });
                const presetCustom =
                  user.customMessages &&
                  ALERTS_TO_SEND.some((alertType) => {
                    return user.alerts.toSend[alertType].methods.custom;
                  });
                if (presetSms || presetEmail || presetCustom) {
                  if (presetSms && !acc.phones.includes(phone)) {
                    acc.phones.push(phone);
                  }

                  if (presetEmail && !acc.emails.includes(user.email)) {
                    acc.emails.push(user.email);
                  }

                  if (
                    presetCustom &&
                    !acc.custom.find(
                      ({ customObject, recipient }) =>
                        customObject === user.customMessages &&
                        recipient === phone,
                    )
                  ) {
                    acc.custom.push({
                      customObject: user.customMessages,
                      recipient: phone,
                    });
                  }

                  if (acc.label === null) {
                    acc.label = user.label[0];
                  } else if (acc.label !== user.label[0]) {
                    acc.label = "";
                  }

                  if (acc.lng === null && user.alerts && user.alerts.language) {
                    acc.lng = user.alerts.language;
                  }
                }
              });

              return acc;
            },
            { phones: [], emails: [], custom: [], label: null, lng: null },
          );

          let sysHealth = Number.parseFloat(data.syshealthpct);
          if (!Number.isFinite(sysHealth)) {
            sysHealth = `NA: '${data.syshealthpct}'`;
          }

          const systemIssues = rawSystemIssues
            .map((issue) => {
              return {
                name: sys.name,
                ...getCleanIssueState(
                  id,
                  issue,
                  label || "",
                  phones.join(LIST_SEPERATOR),
                  emails.join(LIST_SEPERATOR),
                  lng || DEFAULT_LNG,
                  sysHealth,
                  custom,
                  false,
                ),
              };
            })
            .filter((issue) => issue.severity > BASE_SEVERITY_THRESHOLD);

          if (systemIssues.length) {
            issues.push(...systemIssues);
          } else if (
            Number.isFinite(sysHealth) &&
            sysHealth <= BASE_SYSHEALTH_THRESHOLD
          ) {
            badHealthSystems.push({
              name: sys.name,
              ...getCleanIssueState(
                id,
                {
                  dbId: "",
                  dbProps: null,
                  updateDb: false,
                  issueKey: "low health",
                  severity: 0,
                },
                label || "",
                "",
                "",
                lng || DEFAULT_LNG,
                sysHealth,
                custom,
                true,
              ),
            });
          }
        }

        return [issues, badHealthSystems];
      },
      [[], []],
    );

    const result = [...issues, ...badHealthSystems];

    return { result, invalidConfiguration };
  };

  fetchBackendData = () => {
    this.setState(
      { isLoading: true, invalidConfiguration: false },
      async () => {
        try {
          const sysIds = [
            ...Object.keys(this.props.testSystems),
            ...Object.keys(this.props.systems),
          ];
          const url = "/api/insights/search-daily";
          const response = await sendData(
            {
              sysIds,
              dates: [this.state.selectedMoment.format(DB_DATE_FORMAT)],
            },
            url,
            localStorage.getItem(LocalStorageKeys.userToken),
            "post",
          );
          if (!response.ok) {
            throw new Error(response.statusText);
          }
          const result = response.data;
          this.setState((state, props) => {
            const { result: issues, invalidConfiguration } =
              this.extractAllIssues(
                result,
                {
                  ...props.testSystems,
                  ...props.systems,
                },
                props.orgsData,
                props.usersData,
              );

            return {
              error: null,
              data: result,
              issues,
              invalidConfiguration,
            };
          });
        } catch (err) {
          console.error(err);
          this.setState({
            error: err,
            data: null,
            issues: [],
            invalidConfiguration: false,
          });
        } finally {
          this.setState((state) => ({
            isLoading: false,
            shouldSendMessages: true,
            shouldUpdateInsights: true,
            dataUpdateMoment: moment(),
            dataMoment: moment(state.selectedMoment),
          }));
        }
      },
    );
  };

  componentDidMount = () => {
    this._isMounted = true;
    // check if translator is ready, if not force update afterwards
    if ("then" in fixedTs.ready) {
      fixedTs.ready.then(() => this.forceUpdate());
    }

    this.fetchBackendData();
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  updateIssueState = (
    filteredIdx,
    updateKeys,
    updateValues,
    updateDb = false,
  ) => {
    this.setState((state) => {
      if (!this._isMounted || !state.issues) {
        return;
      }
      // allow multiupdates to avoid many consectutive state changes, ad hoc solution
      if (!Array.isArray(updateKeys)) {
        updateKeys = [updateKeys];
        updateValues = [updateValues];
      }
      const issues = [...state.issues];
      const issue = this.getFilteredIssues()[filteredIdx];
      const issueIdx = issues.findIndex((e) => e === issue);
      updateKeys.forEach(
        (key, idx) => (issues[issueIdx][key] = updateValues[idx]),
      );
      // in case severity changes, don't allow send
      if (issues[issueIdx].severity <= BASE_SEVERITY_THRESHOLD) {
        issues[issueIdx].shouldSend = false;
      }
      issue.updateDb = updateDb;
      return {
        issues,
      };
    });
  };

  onSendAll = async () => {
    if (!window.confirm("Send all checked messages?")) {
      console.log("cancelled send all");
      return;
    }

    console.log("updating send queue");
    this.setState((state) => {
      if (!this._isMounted) {
        return;
      }
      const sendQueue = [
        ...state.sendQueue,
        ...state.issues
          .filter((issue) => issue.shouldSend)
          .map((issue) => {
            const phones = issue.phonesString
              .split(LIST_SEPERATOR)
              .map((phone) => phone.trim());
            const emails = issue.emailsString
              .split(LIST_SEPERATOR)
              .map((email) => email.trim());
            const custom = !issue.sendCustom ? [] : issue.customList;
            return new MessageLog(
              issue.id,
              phones,
              emails,
              custom,
              LOG_STATUS.pending,
              this.createMessageString(issue, state.dataMoment),
              issue.label,
              issue.msg,
              state.dataMoment,
              issue.lng,
            );
          }),
      ];
      const issues = state.issues.map((issue) => {
        if (issue.shouldSend) {
          issue.sent = true;
        }
        issue.shouldSend = false;
        return issue;
      });
      return {
        sendQueue,
        issues,
      };
    });
  };

  onPushInsights = async () => {
    if (this.state.pushingInsights) {
      return;
    }

    this.setState({ pushingInsights: true }, () => {
      return this.setState(
        (state) => {
          const updatedInsights = Object.values(
            // filter only updated issues
            state.issues
              .filter((e) => e.updateDb)
              // reduce every updated issue to its new status values
              .reduce((acc, issue) => {
                const { dbId, dbProps, status } = issue;
                if (!acc[dbId]) {
                  const original = state.data.find((e) => e.id === dbId);
                  acc[dbId] = {
                    original,
                    payload: {
                      id: original.id,
                      systemId: original.systemId,
                      date: original.date,
                    },
                  };
                }
                const { original, payload } = acc[dbId];
                if (!Array.isArray(dbProps)) {
                  payload[dbProps.name] = status;
                } else {
                  for (const { name, idx } of dbProps) {
                    if (!Array.isArray(payload[name])) {
                      payload[name] = [...original[name]];
                    }
                    payload[name][idx] = status;
                  }
                }
                return acc;
              }, {}),
          ).map(({ payload: { id, systemId, date, ...rest } }) => {
            return {
              id,
              systemId,
              date,
              payload: rest,
              pushing: true,
              success: false,
              reason: "",
            };
          });

          return {
            pushedInsightsResponses: [
              ...state.pushedInsightsResponses,
              ...updatedInsights,
            ],
          };
        },
        () => {
          const urlPut = "/api/insight/daily";
          const updatedInsights = this.state.pushedInsightsResponses.filter(
            (e) => e.pushing,
          );

          return Promise.allSettled(
            updatedInsights.map(({ id, payload }) => {
              const token = localStorage.getItem(LocalStorageKeys.userToken);
              return sendData(payload, `${urlPut}/${id}`, token, "PUT");
            }),
          )
            .then((responses) => {
              responses.forEach(({ status, value, reason }, idx) => {
                updatedInsights[idx].pushing = false;
                updatedInsights[idx].reason = `${reason || value.statusText}`;
                updatedInsights[idx].success =
                  status === "fulfilled" && value.ok;
              });

              this.setState((state) => {
                const pushedInsightsResponses = [
                  ...state.pushedInsightsResponses,
                ];
                return { pushedInsightsResponses, pushingInsights: false };
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState((state) => {
                const pushedInsightsResponses = [
                  ...state.pushedInsightsResponses,
                ];
                return { pushedInsightsResponses, pushingInsights: false };
              });
            });
        },
      );
    });
  };

  componentDidUpdate = () => {
    if (
      this.state.sendPromiseIdx === null &&
      this.state.sendQueue.some((log) => log.status === LOG_STATUS.pending)
    ) {
      this.setState(
        (state) => {
          // don't move up, data must be fresh at this point
          const sendPromiseIdx = state.sendQueue.findIndex(
            (log) => log.status === LOG_STATUS.pending,
          );
          state.sendQueue[sendPromiseIdx].status = LOG_STATUS.sending;
          return {
            sendPromiseIdx,
          };
        },
        () => this.sendMessage(this.state.sendQueue[this.state.sendPromiseIdx]),
      );
    }
  };

  sendMessage = (messageLog) => {
    sms
      .sendSMS(
        messageLog.body,
        messageLog.recipientNumber,
        messageLog.sender,
        messageLog.type,
        messageLog.lng,
        messageLog.sysId,
        moment(messageLog.dataDate),
        "open",
        messageLog.emails,
        messageLog.body.body,
        null,
        messageLog.customList,
      )
      .then((res) => {
        messageLog.status = LOG_STATUS.success;
        messageLog.messageId = res.map((awsRes) => awsRes.MessageId || "");
        messageLog.messageId.forEach((awsId, idx) => {
          if (!awsId) {
            messageLog.singlePhoneError[idx] = new Error("problem sending");
            messageLog.singlePhoneStatus[idx] = LOG_STATUS.failure;
          } else {
            messageLog.singlePhoneError[idx] = null;
            messageLog.singlePhoneStatus[idx] = LOG_STATUS.success;
          }
        });
      })
      .catch((err) => {
        console.error(err);
        messageLog.status = LOG_STATUS.failure;
        messageLog.error = err;
      })
      .finally(() => {
        if (!this._isMounted) {
          return;
        }
        messageLog.lastEdit = moment();
        this.setState({
          sendPromiseIdx: null,
        });
      });
  };

  getFilteredIssues = () => {
    const { showTestSystems, issues } = this.state;
    const { testSystems } = this.props;
    return issues.filter(
      (i) => showTestSystems || (!showTestSystems && !testSystems[i.id]),
    );
  };

  // returns true only if every issue is checked for sending
  shouldSendAll = () => {
    return this.getFilteredIssues().every(
      (issue) => issue.shouldSend || issue.severity <= BASE_SEVERITY_THRESHOLD,
    );
  };

  // if every issue is checked for send cancells them, checks them otherwise
  flipSendAll = () => {
    this.setState((state) => {
      if (!this._isMounted) {
        return;
      }
      const changeTo = !this.shouldSendAll();
      this.getFilteredIssues().forEach((i) => {
        if (i.severity > BASE_SEVERITY_THRESHOLD) {
          i.shouldSend = changeTo;
        }
      });
      return {
        issues: [...state.issues],
      };
    });
  };

  createMessageString = (issue, dataTime) => {
    const t = fixedTs[issue.lng];
    const extraMessage =
      issue.severity >= ALERT_SEVERITY_THRESHOLD
        ? "alert"
        : issue.severity >= WARNING_SEVERITY_THRESHOLD
          ? "warning"
          : "message";
    const message =
      `${t("system")} ${issue.name}: ${t(extraMessage)} - ` +
      t(issue.msg, issue.params) +
      (moment().isAfter(dataTime, "day")
        ? `\n${dataTime.format(HUMAN_DATE_FORMAT)}`
        : "");
    return {
      body: message,
      params: {
        version: TRANSLATOR_VERSION,
        msg: issue.msg,
        params: issue.params,
      },
    };
  };

  onChangeDataDate = (newDate) => {
    this.setState({ selectedMoment: moment(newDate) });
  };

  handleChangeShowTestSystems = (e) => {
    this.setState((state, props) => {
      const newState = { showTestSystems: !state.showTestSystems };
      if (!newState.showTestSystems) {
        const { testSystems } = props;
        newState.issues = state.issues.map((i) => {
          if (testSystems[i.id]) {
            i.shouldSend = false;
          }
          return i;
        });
      }
      return newState;
    });
  };

  render() {
    const {
      error,
      isLoading,
      selectedMoment,
      dataUpdateMoment,
      dataMoment,
      showTestSystems,
      pushingInsights,
      shouldSendMessages,
      shouldUpdateInsights,
    } = this.state;

    const { systemsErrors } = this.props;
    const showErrorMessage = Object.values(systemsErrors).some(({ errors }) => {
      return !!(errors && Object.keys(errors).length);
    });

    const filteredIssues = this.getFilteredIssues();

    if (error) {
      return (
        <div>
          <Section>Error loading data, check console (F12)</Section>
          <Section>
            <button onClick={this.fetchBackendData} disabled={isLoading}>
              update now
            </button>
          </Section>
        </div>
      );
    }
    if (isLoading || "then" in fixedTs.ready) {
      return <Section>Loading messages data</Section>;
    }

    return (
      <div>
        <Section>
          <h3>Messages log</h3>
          <DatePickerCombo
            selected={selectedMoment}
            disable={isLoading}
            onChange={this.onChangeDataDate}
            format={HUMAN_DATE_FORMAT}
          />
          <span style={{ float: "right" }}>
            <span>Show test systems: </span>
            <input
              type="checkbox"
              checked={showTestSystems}
              onChange={this.handleChangeShowTestSystems}
            />
          </span>
          <div>
            last updated: {dataUpdateMoment.format("DD-MM-YYYY, HH:mm")}
          </div>
          <div>data date: {dataMoment.format("DD-MM-YYYY")}</div>
          {(this.state.invalidConfiguration || showErrorMessage) && (
            <div style={{ color: "red" }}>
              error with configuration, check console
            </div>
          )}
        </Section>
        <Section>
          <button onClick={this.fetchBackendData} disabled={isLoading}>
            update now
          </button>
        </Section>
        {!filteredIssues.length ? (
          <Section>No severe issues</Section>
        ) : (
          <Section>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={this.shouldSendAll()}
                      onChange={this.flipSendAll}
                    />
                  </th>
                  <th>system-id</th>
                  <th>sysh</th>
                  <th>message</th>
                  <th>code</th>
                  <th>status</th>
                  <th>label</th>
                  <th>custom</th>
                  <th>phones</th>
                  <th>emails</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssues.map((issue, idx) => {
                  const isMock = issue.isMock;
                  return (
                    <IssueItem
                      key={idx}
                      disabled={isMock || isLoading || pushingInsights}
                      showCustom={true}
                      thresholdSeverity={BASE_SEVERITY_THRESHOLD}
                      issueData={isMock ? [] : ISSUES_DATA[issue.issueKey]}
                      idx={idx}
                      sysHealth={issue.sysHealth}
                      messageString={
                        isMock
                          ? `Low Health ${issue.sysHealth}`
                          : this.createMessageString(
                              issue,
                              this.state.dataMoment,
                            ).body
                      }
                      updateIssue={isMock ? () => {} : this.updateIssueState}
                      {...issue}
                    />
                  );
                })}
              </tbody>
            </table>
          </Section>
        )}
        <Section>
          <input
            type="checkbox"
            checked={shouldSendMessages}
            onChange={() =>
              this.setState({ shouldSendMessages: !shouldSendMessages })
            }
          />{" "}
          <span>Send messages</span>
        </Section>
        <Section>
          <input
            type="checkbox"
            checked={shouldUpdateInsights}
            onChange={() =>
              this.setState({ shouldUpdateInsights: !shouldUpdateInsights })
            }
          />{" "}
          <span>Update *all* insights</span>
        </Section>
        <Section>
          <button
            disabled={
              pushingInsights || (!shouldUpdateInsights && !shouldSendMessages)
            }
            onClick={async () => {
              if (shouldUpdateInsights) {
                await this.onPushInsights();
              }
              if (shouldSendMessages) {
                await this.onSendAll();
              }

              this.setState({
                shouldSendMessages: false,
                shouldUpdateInsights: false,
              });
            }}
          >
            Perform checked tasks
          </button>
        </Section>
        <Section>
          <h4>Insights queue</h4>
          <ul>
            {this.state.pushedInsightsResponses.map(
              ({ id, systemId, date, pushing, success, reason }, idx) => {
                return (
                  <li key={idx}>
                    database-id: {id}, system: {systemId}, date: {date},{" "}
                    {pushing
                      ? "pushing..."
                      : `success: ${success}${
                          success ? "" : ` reason: ${reason}`
                        }`}
                  </li>
                );
              },
            )}
          </ul>
        </Section>
        <Section>
          <h4>Messages queue</h4>
          <ul>
            {this.state.sendQueue.map((log) => {
              return log.recipientNumber.map((number, idx) => {
                const singleLog = { ...log };
                singleLog.recipientNumber = number;
                singleLog.messageId = log.messageId[idx];
                singleLog.error = log.singlePhoneError[idx];
                singleLog.status = log.singlePhoneStatus[idx];
                return <MessageLogItem key={idx} log={singleLog} />;
              });
            })}
          </ul>
        </Section>
      </div>
    );
  }
}

export default BackendMessagesForm;
